
/* eslint-disable camelcase */

// Agent-facing hospital search (#656), mirroring components/form/Pharmacies.vue.
//
// Structured `hospitals[]` has been written by clients since #598
// (/checkup/hospitals) and is now what the AEP renewal wizard writes too, in
// place of the free-text `preferred_hospital` the old SurveyJS AEP page used.
// Until this existed there was no agent-facing view of that data anywhere.
import { mapGetters } from 'vuex'
import _ from 'lodash'

export default {
    props: {
        value: {
            type: Array,
            default: () => [],
        },
        syncQuery: {
            type: Boolean,
            default: false,
        },
        zipCode: {
            type: String,
            default: null,
        },
        isSurvey: {
            type: Boolean,
            default: false
        },
        showSelectedOnly: {
            type: Boolean,
            default: false
        }
    },
    data () {
        // Seed from the URL only when this form owns the query string.
        // `syncQuery` is a two-way contract, not just a write flag: on the
        // merged Doctors & Hospitals page the providers form owns `query`,
        // `zip_code`, `distance`, `page` and `page_size` — all five names
        // collide, and `$routerx.replaceQuery` merges into one shared query,
        // so reading them here would put the doctor's name in the hospital
        // search box and fire a hospital search for it on mount.
        const routeQuery = this.syncQuery ? (this.$route?.query || {}) : {}
        return {
            results: [],
            total: 0,
            searching: false,
            searchForm: {
                query: routeQuery.query,
                zip_code: this.zipCode || routeQuery.zip_code,
                distance: parseInt(routeQuery.distance || 10),
                page: parseInt(routeQuery.page) || 0,
                page_size: parseInt(routeQuery.page_size) || 10,
            },
        }
    },
    computed: {
        ...mapGetters(['distances']),
        valid () {
            return this.searchForm.zip_code?.length === 5 && this.searchForm.distance > 0
        },
    },
    watch: {
        zipCode (newZipCode) {
            this.$set(this.searchForm, 'zip_code', newZipCode)
        },
        searchForm: {
            deep: true,
            handler: _.debounce(function (searchForm) {
                this.$nextTick(() => {
                    this.syncQuery && this.$routerx.replaceQuery(searchForm)
                })
            }, 300)
        },
    },
    mounted () {
        if (this.valid && this.searchForm.query) {
            return this.search()
        }
    },
    methods: {
        setZipCodeIfUnset (zipcode) {
            if (!this.searchForm.zip_code) {
                this.$set(this.searchForm, 'zip_code', zipcode)
                if (this.valid) this.search()
            }
        },
        async search ({ page = 0, page_size = 10 } = {}) {
            this.$set(this.searchForm, 'page', page)
            this.$set(this.searchForm, 'page_size', page_size)
            this.searching = true
            // Oracle's hospital_search returns a flat result — no plural/singular
            // location split like provider_search — so results need no mapping.
            // It is {results, total, page, pages}: `total`, not `count` (#726).
            const { results, error, total } = await this.$try('searchHospitals', this.searchForm, { spinner: false })
            if (!error) {
                this.results = page === 0 ? results : [].concat(this.results).concat(results || [])
                this.total = total
            }
            this.searching = false
        },
        findHospitalIndex (hospital) {
            // Manual entries carry no provider_id, so fall back to the name —
            // same identity rule as the client-facing HospitalsStep.
            return _.findIndex(this.value, h => (
                hospital.provider_id ? h.provider_id === hospital.provider_id : h.name === hospital.name
            ))
        },
        isHospitalInList (hospital) {
            return this.findHospitalIndex(hospital) > -1
        },
        addHospital (hospital) {
            // Whitelisted to the Hospital serializer (flow/consults/serializers.py:96-124)
            // — raw Oracle results carry extra fields (ownership, lat/lng,
            // address2, zip_code_ext, has_emergency_services,
            // meets_birthing_friendly) that must never be persisted onto the
            // survey. Same whitelist the client-facing HospitalsStep applies.
            const hospitals = this.value.slice(0)
            hospitals.push({
                name: hospital.name,
                provider_id: hospital.provider_id,
                address: hospital.address,
                city: hospital.city,
                state: hospital.state,
                zip_code: hospital.zip_code,
                phone: hospital.phone,
                hospital_type: hospital.hospital_type,
                overall_rating: hospital.overall_rating,
                distance: hospital.distance,
            })
            this.$emit('input', hospitals)
        },
        removeHospital (hospital) {
            const index = this.findHospitalIndex(hospital)
            if (index > -1) {
                const hospitals = this.value.slice(0)
                hospitals.splice(index, 1)
                this.$emit('input', hospitals)
            }
        },
        toggleHospital (hospital) {
            if (this.isHospitalInList(hospital)) {
                return this.removeHospital(hospital)
            } else {
                return this.addHospital(hospital)
            }
        }
    },
}
